import { useEffect, useRef } from 'react';

declare global {
	interface Window {
		uconnect?: {
			analytics?: {
				send_event?: ( name: string, params?: Record<string, unknown> ) => void;
			};
		};
	}
}

function sendEvent( name: string, params?: Record<string, unknown> ) {
	window.uconnect?.analytics?.send_event?.( name, params );
}

interface UseAgentAnalyticsOptions {
	threadId: string;
	messagesSentCount: number;
	isLoggedIn: boolean;
	isResumedThread: boolean;
}

/**
 * Tracks GA4 events for AI search conversation lifecycle:
 * - ai_search_conversation_start: fired on the first message of a conversation
 * - ai_search_conversation_end: fired when the panel closes or a new thread begins
 */
export function useAgentAnalytics( {
	threadId,
	messagesSentCount,
	isLoggedIn,
	isResumedThread,
}: UseAgentAnalyticsOptions ): void {
	const conversationStarted = useRef( false );
	const prevMessagesSentCount = useRef( 0 );
	const prevThreadId = useRef( threadId );

	// Keep refs so effects always read the latest values without re-subscribing.
	const isLoggedInRef = useRef( isLoggedIn );
	isLoggedInRef.current = isLoggedIn;

	const isResumedThreadRef = useRef( isResumedThread );
	isResumedThreadRef.current = isResumedThread;

	const userType = () => ( isLoggedInRef.current ? 'authenticated' : 'anonymous' );

	// Fire conversation_start on the first message of a new conversation.
	useEffect( () => {
		if ( messagesSentCount > prevMessagesSentCount.current ) {
			if ( ! conversationStarted.current ) {
				conversationStarted.current = true;
				sendEvent( 'ai_search_conversation_start', {
					thread_id: threadId,
					user_type: userType(),
				} );
			}
			prevMessagesSentCount.current = messagesSentCount;
		}
	}, [ messagesSentCount, threadId ] ); // eslint-disable-line react-hooks/exhaustive-deps

	// When the threadId changes (startNewChat / clearHistory / resumeThread),
	// end the previous conversation and reset tracking state.
	useEffect( () => {
		if ( threadId === prevThreadId.current ) {
			return;
		}

		if ( conversationStarted.current ) {
			sendEvent( 'ai_search_conversation_end', {
				thread_id: prevThreadId.current,
				message_count: prevMessagesSentCount.current,
				user_type: userType(),
			} );
		}

		prevThreadId.current = threadId;
		// Resumed threads already had a conversation_start in a prior session.
		conversationStarted.current = isResumedThreadRef.current;
		prevMessagesSentCount.current = 0;
	}, [ threadId ] ); // eslint-disable-line react-hooks/exhaustive-deps

	// Fire conversation_end when the component unmounts (i.e. the sidebar panel closes).
	useEffect( () => {
		return () => {
			if ( conversationStarted.current ) {
				sendEvent( 'ai_search_conversation_end', {
					thread_id: prevThreadId.current,
					message_count: prevMessagesSentCount.current,
					user_type: userType(),
				} );
			}
		};
	}, [] ); // eslint-disable-line react-hooks/exhaustive-deps
}

interface UseAgentOpenedEventOptions {
	isOpen: boolean;
	isLoggedIn: boolean;
	siteId?: number;
}

/**
 * Fires ai_search_opened each time the AI search panel transitions to open.
 * GA4's built-in bounce detection can then identify sessions where the user
 * left after this event, without affecting overall site bounce rate metrics.
 */
export function useAgentOpenedEvent( {
	isOpen,
	isLoggedIn,
	siteId,
}: UseAgentOpenedEventOptions ): void {
	useEffect( () => {
		if ( ! isOpen ) {
			return;
		}
		sendEvent( 'ai_search_opened', {
			institution_id: siteId,
			user_type: isLoggedIn ? 'authenticated' : 'anonymous',
			timestamp: Date.now(),
		} );
	}, [ isOpen ] ); // eslint-disable-line react-hooks/exhaustive-deps
}
